import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    LogOut,
    Menu,
    X
} from 'lucide-react';
import LogoDarkHorizontal from '../../../assets/images/logo-dark-horizontal.png';

/**
 * Layout do painel administrativo da plataforma.
 * Arquitetura: Outlet é o pai do conteúdo (sidebar + Outlet).
 */
const PlatformAdminLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userJson = localStorage.getItem('user');

        if (!token || !userJson) {
            navigate('/admin/login');
            return;
        }

        try {
            const user = JSON.parse(userJson);
            if (user.role !== 'PlatformAdmin') {
                navigate('/admin/login');
                return;
            }
            setIsAuthorized(true);
        } catch (error) {
            navigate('/admin/login');
        }
    }, [navigate]);

    if (!isAuthorized) {
        return null; // Or a loading spinner
    }

    const menuItems = [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/users', icon: Users, label: 'Usuários/Tenants' },
        { path: '/admin/plans', icon: CreditCard, label: 'Planos' },
    ];

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin/login');
    };

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Sidebar */}
            <aside
                className={`${isSidebarOpen ? 'w-64' : 'w-20'
                    } bg-slate-900 text-white transition-all duration-300 flex flex-col`}
            >
                <div className="p-4 flex items-center justify-between border-b border-slate-700">
                    {isSidebarOpen ? (
                        <img src={LogoDarkHorizontal} alt="LigChat Admin" className="h-8 w-auto max-w-[140px] object-contain" />
                    ) : (
                        <img src={LogoDarkHorizontal} alt="LC" className="h-8 w-auto max-w-[40px] mx-auto object-contain" />
                    )}
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="p-1 hover:bg-slate-800 rounded"
                    >
                        {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="flex-1 py-6">
                    <ul className="space-y-2 px-2">
                        {menuItems.map((item) => (
                            <li key={item.path}>
                                <button
                                    onClick={() => navigate(item.path)}
                                    className={`w-full flex items-center p-3 rounded-lg transition-colors ${location.pathname === item.path
                                        ? 'bg-blue-600 text-white'
                                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                        }`}
                                >
                                    <item.icon size={20} />
                                    {isSidebarOpen && <span className="ml-3">{item.label}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="p-4 border-t border-slate-700">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center p-3 text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <LogOut size={20} />
                        {isSidebarOpen && <span className="ml-3">Sair</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                <header className="bg-white shadow-sm p-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {menuItems.find(m => m.path === location.pathname)?.label || 'Painel Admin'}
                    </h2>
                    <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">Platform Administrator</span>
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            PA
                        </div>
                    </div>
                </header>

                <div className="p-6">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default PlatformAdminLayout;
